import React, { useState, useEffect, useMemo } from 'react';

const DocumentationContent = ({ pagesPermissions = [], user = null }) => {
  const [activeSection, setActiveSection] = useState(null);
  const [tocVisible, setTocVisible] = useState(true);

  // Scroll to section when activeSection changes
  useEffect(() => {
    if (activeSection) {
      const element = document.getElementById(activeSection);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [activeSection]);

  // Mapping between page slug and documentation section ID
  const pageToSectionMap = {
    'smart-parser': 'pdf-parser',
    'financial-analysis': 'financial-analysis',
    'audit-plan-analysis': 'audit-plan',
    'rnd-prompting': 'rnd-prompting',
    'llm-config': 'llm-config',
    'analytics': 'analytics',
    'history': 'history',
    'admin-area': 'admin-area'
  };

  // Get accessible pages slugs
  const accessiblePageSlugs = useMemo(() => {
    if (!pagesPermissions || pagesPermissions.length === 0) {
      return [];
    }

    return pagesPermissions
      .filter(pageWithPerm => {
        return pageWithPerm.permissions && 
               pageWithPerm.permissions.can_view === true && 
               pageWithPerm.page && 
               pageWithPerm.page.is_active;
      })
      .map(pageWithPerm => pageWithPerm.page.slug);
  }, [pagesPermissions]);

  // Check if user has access to a specific page
  const hasAccess = useMemo(() => {
    const accessMap = {};
    
    // Always available sections
    accessMap['getting-started'] = true;
    accessMap['dashboard'] = true;
    accessMap['my-account'] = true;
    accessMap['tips'] = true;

    // Check admin area access
    const isSuperuser = user?.is_superuser || false;
    const hasAdminAccess = isSuperuser || 
      (pagesPermissions && pagesPermissions.some(pageWithPerm => 
        pageWithPerm.page && 
        pageWithPerm.page.slug === 'admin-area' &&
        pageWithPerm.permissions && 
        pageWithPerm.permissions.can_view === true &&
        pageWithPerm.page.is_active
      ));
    accessMap['admin-area'] = hasAdminAccess;

    // Map accessible pages to section IDs
    Object.keys(pageToSectionMap).forEach(slug => {
      const sectionId = pageToSectionMap[slug];
      accessMap[sectionId] = accessiblePageSlugs.includes(slug);
    });

    return accessMap;
  }, [accessiblePageSlugs, user, pagesPermissions, pageToSectionMap]);

  // Filtered Table of Contents based on user access
  const tableOfContents = useMemo(() => {
    const allToc = [
      { id: 'getting-started', title: 'Getting Started', icon: 'fa-rocket' },
      { id: 'dashboard', title: 'Dashboard', icon: 'fa-home' },
      { id: 'pdf-parser', title: 'PDF Parser Smart', icon: 'fa-robot', pageSlug: 'smart-parser' },
      { id: 'financial-analysis', title: 'Laporan Keuangan Analysis', icon: 'fa-file-invoice-dollar', pageSlug: 'financial-analysis' },
      { id: 'audit-plan', title: 'Audit Plan Analysis', icon: 'fa-clipboard-check', pageSlug: 'audit-plan-analysis' },
      { id: 'rnd-prompting', title: 'RnD Prompting', icon: 'fa-flask', pageSlug: 'rnd-prompting' },
      { id: 'llm-config', title: 'LLM Configuration', icon: 'fa-microchip', pageSlug: 'llm-config' },
      { id: 'analytics', title: 'Analytics', icon: 'fa-chart-line', pageSlug: 'analytics' },
      { id: 'history', title: 'History', icon: 'fa-clock-rotate-left', pageSlug: 'history' },
      { id: 'admin-area', title: 'Admin Area', icon: 'fa-shield-halved', pageSlug: 'admin-area' },
      { id: 'my-account', title: 'My Account', icon: 'fa-user' },
      { id: 'tips', title: 'Tips & Best Practices', icon: 'fa-lightbulb' }
    ];

    // Filter based on access
    return allToc.filter(item => hasAccess[item.id] !== false);
  }, [hasAccess]);

  return (
    <main className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Table of Contents Sidebar */}
          <div className={`lg:col-span-1 ${tocVisible ? '' : 'hidden'} lg:block`}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 sticky top-6 transition-colors duration-300">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center transition-colors duration-300">
                  <i className="fa-solid fa-list mr-2 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                  Daftar Isi
                </h2>
                <button
                  onClick={() => setTocVisible(false)}
                  className="lg:hidden text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
              <nav className="space-y-1">
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center text-sm ${
                      activeSection === item.id
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <i className={`fa-solid ${item.icon} mr-2 text-xs`}></i>
                    {item.title}
                  </button>
                ))}
              </nav>
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm transition-colors flex items-center justify-center"
                >
                  <i className="fa-solid fa-arrow-up mr-2"></i>
                  Kembali ke Atas
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-8 transition-colors duration-300">
              {/* Header */}
              <div className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-book mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Dokumentasi Pengguna
                  </h1>
                  <button
                    onClick={() => setTocVisible(!tocVisible)}
                    className="lg:hidden px-4 py-2 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-lg text-sm transition-colors"
                  >
                    <i className="fa-solid fa-list mr-2"></i>
                    Daftar Isi
                  </button>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-lg transition-colors duration-300">
                  Panduan lengkap untuk menggunakan semua fitur Tabulara dari sisi antarmuka pengguna (Frontend).
                </p>
              </div>

              <div className="prose max-w-none">
                {/* Getting Started */}
                <section id="getting-started" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-rocket mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Getting Started
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Selamat datang di Tabulara! Dokumentasi ini akan memandu Anda menggunakan semua fitur yang tersedia 
                      dari sisi antarmuka pengguna. Setiap fitur dirancang untuk membantu Anda bekerja lebih efisien dengan 
                      bantuan teknologi AI.
                    </p>
                    
                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded-r-lg transition-colors duration-300">
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">
                        <i className="fa-solid fa-info-circle text-blue-600 dark:text-blue-400 mr-2 transition-colors duration-300"></i>
                        Catatan Penting
                      </h3>
                      <p className="text-sm">
                        Dokumentasi ini hanya mencakup penggunaan antarmuka pengguna (frontend). Untuk informasi teknis 
                        mengenai API backend, silakan hubungi administrator atau tim development.
                      </p>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Persiapan Awal</h3>
                    <ol className="list-decimal list-inside space-y-2 ml-4">
                      <li>Pastikan Anda sudah memiliki akun dan telah melakukan login</li>
                      <li>Periksa status akun Anda di dashboard untuk memastikan akun aktif</li>
                      <li>Familiarize yourself dengan menu sidebar untuk navigasi</li>
                      <li>Pastikan browser Anda menggunakan versi terbaru untuk pengalaman optimal</li>
                    </ol>
                  </div>
                </section>

                {/* Dashboard */}
                <section id="dashboard" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-home mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Dashboard
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Dashboard adalah halaman utama yang akan muncul setiap kali Anda login. Halaman ini memberikan 
                      gambaran cepat tentang status akun Anda dan akses ke semua fitur yang tersedia.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Fitur Dashboard</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center transition-colors duration-300">
                          <i className="fa-solid fa-chart-bar text-blue-500 mr-2"></i>
                          Quick Stats
                        </h4>
                        <ul className="text-sm space-y-1 ml-6">
                          <li>• Total Fitur Akses: Jumlah fitur yang dapat Anda akses</li>
                          <li>• Status Akun: Aktif atau Tidak Aktif</li>
                          <li>• Role: Superuser atau User</li>
                        </ul>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center transition-colors duration-300">
                          <i className="fa-solid fa-rocket text-green-500 mr-2"></i>
                          Fitur yang Dapat Diakses
                        </h4>
                        <ul className="text-sm space-y-1 ml-6">
                          <li>• Grid card untuk setiap fitur</li>
                          <li>• Informasi permissions (View, Create, Edit, Delete)</li>
                          <li>• Klik langsung untuk membuka fitur</li>
                        </ul>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <ol className="list-decimal list-inside space-y-2 ml-4">
                      <li>Setelah login, Anda akan otomatis diarahkan ke Dashboard</li>
                      <li>Review Quick Stats untuk melihat informasi akun Anda</li>
                      <li>Scroll ke bawah untuk melihat semua fitur yang dapat diakses</li>
                      <li>Klik pada card fitur untuk langsung membuka halaman fitur tersebut</li>
                      <li>Gunakan section "Butuh Bantuan?" di bagian bawah untuk mendapatkan bantuan</li>
                    </ol>
                  </div>
                </section>

                {/* PDF Parser Smart */}
                {hasAccess['pdf-parser'] && (
                <section id="pdf-parser" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-robot mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    PDF Parser Smart
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      PDF Parser Smart adalah fitur canggih untuk mengekstrak data dari dokumen bank statement menggunakan 
                      teknologi AI. Fitur ini mendukung berbagai format dokumen dan dapat mendeteksi transaksi secara otomatis.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <ol className="list-decimal list-inside space-y-3 ml-4">
                      <li>
                        <strong>Upload File</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Klik tombol "Upload" atau drag & drop file PDF ke area upload</li>
                          <li>File yang didukung: PDF (bank statements)</li>
                          <li>Maksimal ukuran file: 50MB</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Proses Parsing</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Sistem akan melakukan Optical Character Recognition (OCR) untuk membaca teks</li>
                          <li>Teknologi AI akan melakukan parsing dan ekstraksi data secara otomatis</li>
                          <li>Proses akan menampilkan status setiap tahap secara real-time</li>
                          <li>Tunggu hingga proses selesai (biasanya 1-3 menit tergantung ukuran file)</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Review Hasil</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Setelah selesai, tabel transaksi akan ditampilkan</li>
                          <li>Review data yang diekstrak untuk memastikan akurasi</li>
                          <li>Fitur Fraud Detection akan menampilkan analisis transaksi mencurigakan</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Download atau Export</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Gunakan tombol export untuk mengunduh hasil dalam format Excel atau CSV</li>
                          <li>Data dapat di-copy langsung dari tabel jika diperlukan</li>
                        </ul>
                      </li>
                    </ol>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Fitur Utama</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Multi-Bank Support</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">Mendukung berbagai format bank statement dari bank yang berbeda</p>
                      </div>
                      <div className="border-l-4 border-green-500 dark:border-green-400 pl-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Fraud Detection</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">Otomatis mendeteksi transaksi yang mencurigakan berdasarkan pola tertentu</p>
                      </div>
                      <div className="border-l-4 border-purple-500 dark:border-purple-400 pl-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Batch Processing</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">Dapat memproses multiple file sekaligus untuk efisiensi</p>
                      </div>
                      <div className="border-l-4 border-orange-500 dark:border-orange-400 pl-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Real-time Status</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">Progress indicator untuk setiap tahap proses parsing</p>
                      </div>
                    </div>
                  </div>
                </section>
                )}

                {/* Financial Analysis */}
                {hasAccess['financial-analysis'] && (
                <section id="financial-analysis" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-file-invoice-dollar mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Laporan Keuangan Analysis
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Fitur ini dirancang untuk menganalisis laporan keuangan (Neraca & Laba Rugi) secara otomatis. 
                      Sistem akan mengekstrak metrik finansial dan melakukan perhitungan rasio keuangan secara otomatis.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <ol className="list-decimal list-inside space-y-3 ml-4">
                      <li>
                        <strong>Upload Laporan Keuangan</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Upload file PDF yang berisi Neraca dan Laba Rugi</li>
                          <li>Dapat upload beberapa file untuk analisis multi-tahun</li>
                          <li>Maksimal total ukuran: 100MB untuk semua file</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Proses Analisis AI</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Sistem melakukan Optical Character Recognition (OCR) untuk membaca dokumen</li>
                          <li>Ekstraksi data dilakukan dengan teknologi AI secara otomatis</li>
                          <li>Data diorganisir per tahun untuk analisis tren</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Review dan Edit Data</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Lihat data yang diekstrak di tab "Extract LLM"</li>
                          <li>Klik pada nilai untuk mengedit jika diperlukan</li>
                          <li>Perubahan akan otomatis mengupdate kalkulasi</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Analisis Lengkap</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Tab "Analisis Lengkap" menampilkan Financial Highlights, Ratios, dan WI Cycle</li>
                          <li>Tab "Financial Highlight" menampilkan ringkasan utama</li>
                          <li>Tab "YoY Analysis" untuk melihat perubahan year-over-year</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Generate AI Insights</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Klik tombol "Generate AI Insights" untuk mendapatkan analisis mendalam</li>
                          <li>AI akan memberikan insight dan rekomendasi berdasarkan data</li>
                        </ul>
                      </li>
                    </ol>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Metrik yang Diekstrak</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 transition-colors duration-300">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <h4 className="font-semibold mb-2">Neraca:</h4>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Jumlah Asset Lancar</li>
                            <li>Jumlah Kewajiban Jangka Panjang</li>
                            <li>Jumlah Kewajiban Jangka Pendek</li>
                            <li>Piutang Usaha</li>
                            <li>Persediaan</li>
                            <li>Hutang Usaha</li>
                            <li>Jumlah Equitas</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Laba Rugi:</h4>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Pendapatan / Penjualan</li>
                            <li>HPP / COGS</li>
                            <li>Laba Kotor</li>
                            <li>Laba Usaha</li>
                            <li>Laba Sebelum Pajak</li>
                            <li>Laba Bersih</li>
                            <li>Beban Penyusutan</li>
                            <li>Bunga Pinjaman</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Financial Ratios yang Dihitung</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li><strong>Revenue Growth:</strong> Pertumbuhan pendapatan year-over-year</li>
                      <li><strong>Current Ratio:</strong> Rasio likuiditas (Current Assets / Current Liabilities)</li>
                      <li><strong>Interest Cover:</strong> Kemampuan membayar bunga</li>
                      <li><strong>EBITDA:</strong> Earnings Before Interest, Taxes, Depreciation, and Amortization</li>
                      <li><strong>Debt Service Ratio (DSR):</strong> Rasio kemampuan membayar utang</li>
                      <li><strong>Leverage Ratio:</strong> Rasio utang terhadap equitas</li>
                    </ul>
                  </div>
                </section>
                )}

                {/* Audit Plan Analysis */}
                {hasAccess['audit-plan'] && (
                <section id="audit-plan" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-clipboard-check mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Audit Plan Analysis
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Fitur ini membantu menganalisis dokumen regulasi dan internal untuk perencanaan audit. 
                      Mendukung dua tipe analisis: Regulatory Analysis dan High Risk Process Analysis.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Jenis Analisis</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="border border-blue-200 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Regulatory Analysis</h4>
                        <p className="text-sm mb-2 text-gray-700 dark:text-gray-300 transition-colors duration-300">Untuk dokumen OJK/BI:</p>
                        <ul className="list-disc list-inside ml-4 text-sm space-y-1">
                          <li>Mengekstrak audit requirements</li>
                          <li>Technology security testing requirements</li>
                          <li>Compliance checklist</li>
                        </ul>
                      </div>
                      <div className="border border-purple-200 dark:border-purple-700 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/30 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">High Risk Process Analysis</h4>
                        <p className="text-sm mb-2 text-gray-700 dark:text-gray-300 transition-colors duration-300">Untuk dokumen internal:</p>
                        <ul className="list-disc list-inside ml-4 text-sm space-y-1">
                          <li>SOP, WI, Policy, Guidelines</li>
                          <li>Identifikasi proses berisiko tinggi</li>
                          <li>Risk assessment</li>
                        </ul>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <ol className="list-decimal list-inside space-y-3 ml-4">
                      <li>
                        <strong>Pilih Tipe Analisis</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Pilih "Regulatory Analysis" untuk dokumen regulasi</li>
                          <li>Pilih "High Risk Process Analysis" untuk dokumen internal</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Upload Dokumen</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Format yang didukung: PDF, DOC, DOCX, TXT</li>
                          <li>Maksimal ukuran per file: 50MB</li>
                          <li>Dapat upload multiple file sekaligus</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Proses Analisis</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Sistem menggunakan teknologi AI untuk analisis dokumen</li>
                          <li>Progress indicator akan menampilkan setiap tahap</li>
                          <li>Proses biasanya memakan waktu 2-5 menit</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Review Hasil</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Hasil ditampilkan dalam format tabel</li>
                          <li>Dapat melakukan export ke Excel</li>
                          <li>Review dan validasi hasil analisis</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                </section>
                )}

                {/* RnD Prompting */}
                {hasAccess['rnd-prompting'] && (
                <section id="rnd-prompting" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-flask mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    RnD Prompting
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Fitur untuk testing dan eksperimen dengan AI prompts. Berguna untuk penelitian dan pengembangan 
                      prompt yang optimal untuk berbagai use case.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <ol className="list-decimal list-inside space-y-3 ml-4">
                      <li>
                        <strong>Masukkan Custom Prompt</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Masukkan prompt yang ingin Anda test di text area</li>
                          <li>Prompt harus jelas dan spesifik untuk hasil optimal</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Upload File (Opsional)</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Dapat upload file untuk dijadikan context</li>
                          <li>Format yang didukung: PDF, DOC, DOCX, TXT</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Proses dengan AI</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Klik tombol "Process" untuk menjalankan prompt</li>
                          <li>AI akan memproses sesuai dengan prompt Anda</li>
                          <li>Hasil akan ditampilkan di bawah</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Review dan Iterasi</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Review hasil yang didapat</li>
                          <li>Modifikasi prompt jika diperlukan</li>
                          <li>Ulangi proses untuk mendapatkan hasil terbaik</li>
                        </ul>
                      </li>
                    </ol>

                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-500 dark:border-yellow-400 p-4 rounded-r-lg mt-4 transition-colors duration-300">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        <i className="fa-solid fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 mr-2 transition-colors duration-300"></i>
                        Tips untuk Prompt yang Baik
                      </h4>
                      <ul className="text-sm space-y-1 ml-4">
                        <li>• Gunakan instruksi yang jelas dan spesifik</li>
                        <li>• Berikan context yang cukup</li>
                        <li>• Tentukan format output yang diinginkan</li>
                        <li>• Test dengan berbagai variasi prompt</li>
                      </ul>
                    </div>
                  </div>
                </section>
                )}

                {/* LLM Configuration */}
                {hasAccess['llm-config'] && (
                <section id="llm-config" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-microchip mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    LLM Configuration
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Halaman konfigurasi untuk mengatur model AI dan API keys yang digunakan oleh sistem. 
                      Konfigurasi ini akan mempengaruhi semua fitur yang menggunakan AI.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Pengaturan yang Tersedia</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Provider AI</h4>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Gemini (Google)</li>
                          <li>• Groq (Llama models)</li>
                        </ul>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Model Selection</h4>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Gemini: gemini-2.5-pro, gemini-2.5-flash, dll</li>
                          <li>• Groq: llama-3.3-70b-versatile, dll</li>
                        </ul>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Cara Mengkonfigurasi</h3>
                    <ol className="list-decimal list-inside space-y-3 ml-4">
                      <li>
                        <strong>Pilih Provider</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Pilih provider yang ingin digunakan (Gemini atau Groq)</li>
                          <li>Setiap provider memiliki model yang berbeda</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Masukkan API Key</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Masukkan API key untuk provider yang dipilih</li>
                          <li>API key akan disimpan secara lokal di browser Anda</li>
                          <li>Jangan bagikan API key Anda kepada orang lain</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Pilih Model</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Pilih model AI yang ingin digunakan</li>
                          <li>Setiap model memiliki karakteristik berbeda</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Save Configuration</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Klik "Save" untuk menyimpan konfigurasi</li>
                          <li>Konfigurasi akan diterapkan pada semua fitur</li>
                        </ul>
                      </li>
                    </ol>

                    <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 dark:border-red-400 p-4 rounded-r-lg mt-4 transition-colors duration-300">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        <i className="fa-solid fa-shield-halved text-red-600 dark:text-red-400 mr-2 transition-colors duration-300"></i>
                        Keamanan API Key
                      </h4>
                      <ul className="text-sm space-y-1 ml-4">
                        <li>• API key disimpan di localStorage browser Anda</li>
                        <li>• Jangan pernah membagikan API key kepada siapa pun</li>
                        <li>• Hapus API key jika browser digunakan bersama</li>
                        <li>• Ganti API key secara berkala untuk keamanan</li>
                      </ul>
                    </div>
                  </div>
                </section>
                )}

                {/* Analytics */}
                {hasAccess['analytics'] && (
                <section id="analytics" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-chart-line mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Analytics
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Dashboard analytics yang menampilkan statistik penggunaan sistem, aktivitas pengguna, 
                      dan insights dari penggunaan berbagai fitur.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Informasi yang Ditampilkan</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li><strong>User Statistics:</strong> Jumlah pengguna aktif, total pengguna, dll</li>
                      <li><strong>Feature Usage:</strong> Statistik penggunaan setiap fitur</li>
                      <li><strong>Activity Metrics:</strong> Total aktivitas, token usage, dll</li>
                      <li><strong>Trends:</strong> Grafik tren penggunaan dari waktu ke waktu</li>
                    </ul>

                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded-r-lg mt-4 transition-colors duration-300">
                      <p className="text-sm">
                        <strong>Catatan:</strong> Fitur Analytics biasanya hanya dapat diakses oleh administrator 
                        atau superuser. Jika Anda tidak melihat menu ini, hubungi administrator untuk mendapatkan akses.
                      </p>
                    </div>
                  </div>
                </section>
                )}

                {/* History */}
                {hasAccess['history'] && (
                <section id="history" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-clock-rotate-left mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    History
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Halaman History menampilkan riwayat semua aktivitas dan analisis yang telah Anda lakukan. 
                      Berguna untuk melihat kembali hasil analisis sebelumnya atau mencari data historis.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Fitur History</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li><strong>Activity List:</strong> Daftar semua aktivitas yang telah dilakukan</li>
                      <li><strong>Search & Filter:</strong> Cari aktivitas berdasarkan tanggal, fitur, atau keyword</li>
                      <li><strong>View Details:</strong> Klik pada aktivitas untuk melihat detail lengkap</li>
                      <li><strong>Download Results:</strong> Download hasil analisis yang sudah pernah dilakukan</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <ol className="list-decimal list-inside space-y-2 ml-4">
                      <li>Buka menu "History" dari sidebar</li>
                      <li>Scroll atau gunakan filter untuk menemukan aktivitas yang dicari</li>
                      <li>Klik pada aktivitas untuk melihat detail</li>
                      <li>Gunakan tombol download jika ingin mengunduh hasil</li>
                    </ol>
                  </div>
                </section>
                )}

                {/* Admin Area */}
                {hasAccess['admin-area'] && (
                <section id="admin-area" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-shield-halved mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Admin Area
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-500 dark:border-yellow-400 p-4 rounded-r-lg transition-colors duration-300">
                      <p className="text-sm">
                        <strong>Catatan:</strong> Fitur Admin Area hanya dapat diakses oleh administrator atau superuser. 
                        Jika Anda tidak memiliki akses, hubungi administrator.
                      </p>
                    </div>

                    <p className="mt-4">
                      Admin Area adalah panel kontrol untuk mengelola users, groups, dan permissions. Fitur ini memungkinkan 
                      administrator untuk mengatur akses pengguna ke berbagai fitur sistem.
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Fitur Manajemen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">User Management</h4>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Create user baru</li>
                          <li>• Edit informasi user</li>
                          <li>• Aktifkan/nonaktifkan user</li>
                          <li>• Reset password</li>
                          <li>• Set expired date</li>
                        </ul>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Group Management</h4>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Create group baru</li>
                          <li>• Edit group</li>
                          <li>• Assign users ke group</li>
                          <li>• Aktifkan/nonaktifkan group</li>
                        </ul>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Page Permissions</h4>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Atur permissions per page</li>
                          <li>• View, Create, Edit, Delete</li>
                          <li>• Set permissions per group</li>
                        </ul>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Cara Menggunakan</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">1. User Management</h4>
                        <ul className="list-disc list-inside ml-6 text-sm space-y-1">
                          <li>Buka tab "User Management"</li>
                          <li>Klik "Add User" untuk membuat user baru</li>
                          <li>Isi form dan klik "Save"</li>
                          <li>Gunakan tombol edit/delete untuk mengelola user yang ada</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">2. Group Management</h4>
                        <ul className="list-disc list-inside ml-6 text-sm space-y-1">
                          <li>Buka tab "Group Management"</li>
                          <li>Create group baru atau edit group yang ada</li>
                          <li>Assign users ke group melalui tab "User Groups"</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">3. Page Management</h4>
                        <ul className="list-disc list-inside ml-6 text-sm space-y-1">
                          <li>Buka tab "Page Management"</li>
                          <li>Pilih group yang ingin diatur permissions-nya</li>
                          <li>Set permissions untuk setiap page (View, Create, Edit, Delete)</li>
                          <li>Klik "Save" untuk menyimpan perubahan</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>
                )}

                {/* My Account */}
                <section id="my-account" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-user mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    My Account
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <p>
                      Halaman My Account memungkinkan Anda untuk melihat informasi akun Anda dan mengubah password. 
                      Akses melalui menu dropdown di header (klik nama Anda di pojok kanan atas).
                    </p>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Informasi yang Ditampilkan</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li><strong>Username:</strong> Username akun Anda</li>
                      <li><strong>Email:</strong> Alamat email yang terdaftar</li>
                      <li><strong>Full Name:</strong> Nama lengkap Anda</li>
                      <li><strong>Status:</strong> Status akun (Aktif/Tidak Aktif)</li>
                      <li><strong>Role:</strong> Role Anda (Superuser/User)</li>
                      <li><strong>Last Login:</strong> Waktu login terakhir</li>
                      <li><strong>Created At:</strong> Tanggal akun dibuat</li>
                      <li><strong>Updated At:</strong> Tanggal terakhir update</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Mengubah Password</h3>
                    <ol className="list-decimal list-inside space-y-3 ml-4">
                      <li>
                        <strong>Scroll ke Form Change Password</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Form terletak di bagian bawah halaman</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Masukkan Password Lama</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Masukkan password saat ini untuk verifikasi</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Masukkan Password Baru</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Password baru minimal 8 karakter</li>
                          <li>Gunakan kombinasi huruf, angka, dan karakter khusus</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Konfirmasi Password Baru</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Masukkan ulang password baru untuk konfirmasi</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Klik "Change Password"</strong>
                        <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                          <li>Sistem akan memvalidasi dan menyimpan password baru</li>
                          <li>Anda akan melihat notifikasi sukses atau error</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                </section>

                {/* Tips & Best Practices */}
                <section id="tips" className="mb-12 scroll-mt-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                    <i className="fa-solid fa-lightbulb mr-3 text-blue-600 dark:text-blue-400 transition-colors duration-300"></i>
                    Tips & Best Practices
                  </h2>
                  <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Upload File</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li>Gunakan file dengan kualitas scan yang baik untuk hasil OCR yang optimal</li>
                      <li>Pastikan file tidak password-protected</li>
                      <li>Untuk batch processing, upload file dengan format yang konsisten</li>
                      <li>Periksa ukuran file sebelum upload (maksimal sesuai limit yang ditentukan)</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Menggunakan AI Features</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li>Berikan prompt yang jelas dan spesifik untuk hasil yang lebih baik</li>
                      <li>Review hasil AI sebelum digunakan untuk keputusan penting</li>
                      <li>Edit manual data yang diekstrak jika diperlukan untuk akurasi</li>
                      <li>Gunakan fitur History untuk melihat kembali hasil sebelumnya</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Keamanan</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li>Jangan bagikan kredensial login Anda</li>
                      <li>Ganti password secara berkala</li>
                      <li>Logout setelah selesai menggunakan sistem, terutama di komputer bersama</li>
                      <li>Jaga kerahasiaan API keys jika menggunakan konfigurasi LLM sendiri</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Performance</h3>
                    <ul className="list-disc list-inside ml-6 space-y-2">
                      <li>Proses AI membutuhkan waktu, bersabarlah dan jangan refresh halaman</li>
                      <li>Gunakan fitur batch upload untuk multiple file</li>
                      <li>Close tab yang tidak digunakan untuk menghemat memory browser</li>
                      <li>Gunakan browser yang ter-update untuk performa optimal</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mt-6 mb-3 transition-colors duration-300">Troubleshooting</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 transition-colors duration-300">
                      <ul className="list-disc list-inside ml-6 space-y-2 text-sm">
                        <li><strong>File tidak bisa diupload:</strong> Periksa ukuran file dan format yang didukung</li>
                        <li><strong>Error saat proses:</strong> Refresh halaman dan coba lagi, atau hubungi support</li>
                        <li><strong>Hasil tidak akurat:</strong> Coba edit manual atau upload ulang dengan file yang lebih jelas</li>
                        <li><strong>Login issues:</strong> Clear cache browser atau hubungi administrator</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6 text-center transition-colors duration-300">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">
                      <i className="fa-solid fa-circle-question text-blue-600 dark:text-blue-400 mr-2 transition-colors duration-300"></i>
                      Masih Butuh Bantuan?
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 transition-colors duration-300">
                      Jika Anda tidak menemukan jawaban yang dicari dalam dokumentasi ini, 
                      silakan hubungi tim support kami melalui halaman "Butuh Bantuan?" di Dashboard.
                    </p>
                    <button
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <i className="fa-solid fa-arrow-up mr-2"></i>
                      Kembali ke Atas
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DocumentationContent;
